import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Stack, ToggleButton,
  ToggleButtonGroup, Alert,
} from '@mui/material';
import {
  RoomService as ComandaIcon, FiberManualRecord as DotIcon, CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { ordersApi } from '../api/orders.api';
import { getSocket } from '../api/socket';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Pantalla del mozo (modo "comanda al mozo"): pedidos registrados en caja que
// aun no se entregan al cliente. El mozo identifica sus comandas y confirma la
// entrega (estado "servido"). El cobro y cierre de cuenta siguen en Caja/POS.

const PENDING_STATUSES = ['pendiente', 'en_preparacion', 'listo'];

const STATUS_META: Record<string, { label: string; color: string }> = {
  pendiente: { label: 'POR ENTREGAR', color: '#EF4444' },
  en_preparacion: { label: 'EN PREPARACION', color: '#F59E0B' },
  listo: { label: 'LISTO', color: '#10B981' },
};

const minutesAgo = (date: string) => Math.max(0, Math.round((Date.now() - new Date(date).getTime()) / 60000));

const Comandas: React.FC = () => {
  const { user } = useAuth();
  const isMozo = user?.role === 'mozo';
  // El mozo ve por defecto solo sus comandas; caja/admin ven todas.
  const [scope, setScope] = useState<'mias' | 'todas'>(isMozo ? 'mias' : 'todas');
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [flowMode, setFlowMode] = useState<'tradicional' | 'mozo' | null>(null);
  const [delivering, setDelivering] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await ordersApi.getActive();
      const pending = (res.data || [])
        .filter((o: any) => PENDING_STATUSES.includes(o.status))
        .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setOrders(pending);
    } catch (err) {
      console.error('Error cargando comandas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    ordersApi.getFlowConfig().then(r => setFlowMode(r.data.mode)).catch(() => setFlowMode(null));
  }, []);

  useEffect(() => {
    load();
    // Tiempo real: el backend emite orders:changed al crear, agregar o cambiar estado.
    const socket = getSocket();
    if (!socket.connected) socket.connect();
    const handler = () => load();
    socket.on('orders:changed', handler);
    // Respaldo por si se cae el socket
    pollRef.current = setInterval(load, 15000);
    return () => {
      socket.off('orders:changed', handler);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const confirmDelivery = async (order: any) => {
    if (delivering) return;
    setDelivering(order.id);
    setError(null);
    try {
      await ordersApi.markServed(order.id);
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('. ') : msg || 'No se pudo confirmar la entrega.');
    } finally {
      setDelivering(null);
    }
  };

  if (loading && orders.length === 0) return <LoadingSpinner />;

  const visible = scope === 'mias' ? orders.filter(o => o.waiterId === user?.id) : orders;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ComandaIcon sx={{ fontSize: 32, color: '#059669' }} />
          <Typography variant="h4">Comandas</Typography>
          <Chip label={`${visible.length} por entregar`} size="small" sx={{ ml: 1 }} />
        </Box>
        <ToggleButtonGroup value={scope} exclusive size="small" onChange={(_e, v) => v && setScope(v)}>
          <ToggleButton value="mias">Mis comandas</ToggleButton>
          <ToggleButton value="todas">Todas</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {flowMode === 'tradicional' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          El sistema esta en modo tradicional (cocina/bar). Esta pantalla esta pensada para el modo
          "comanda al mozo", pero puedes usarla para confirmar entregas.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {visible.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: '#9CA3AF' }}>
          <DotIcon sx={{ fontSize: 48, color: '#10B981' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>Sin comandas pendientes</Typography>
          <Typography variant="body2">
            {scope === 'mias' ? 'No tienes pedidos por entregar.' : 'No hay pedidos por entregar.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {visible.map((order) => {
            const meta = STATUS_META[order.status] || { label: order.status, color: '#9CA3AF' };
            const items = (order.items || []).filter((it: any) => it.status !== 'cancelado');
            const mine = order.waiterId === user?.id;
            return (
              <Grid key={order.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ borderTop: `4px solid ${meta.color}`, ...(mine ? { boxShadow: '0 0 0 2px #05966955' } : {}) }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{order.code}</Typography>
                      <Chip label={`Mesa ${order.table?.number ?? order.tableId}`} size="small" />
                    </Box>
                    {order.customerName && (
                      <Typography sx={{ fontWeight: 700 }}>Cliente: {order.customerName}</Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', color: '#6B7280' }}>
                      Mozo: {order.waiter?.fullName ?? '—'}{mine ? ' (tu)' : ''} · hace {minutesAgo(order.createdAt)} min
                    </Typography>
                    <Chip label={meta.label} size="small"
                      sx={{ mt: 1, bgcolor: meta.color, color: '#fff', fontWeight: 700 }} />
                    {order.notes && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#92400E', fontStyle: 'italic' }}>
                        Nota: {order.notes}
                      </Typography>
                    )}
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {items.map((it: any) => (
                        <Box key={it.id}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {it.quantity}x {it.recipe?.name ?? `Item #${it.recipeId}`}
                          </Typography>
                          {it.notes && (
                            <Typography variant="caption" sx={{ color: '#92400E' }}>&gt; {it.notes}</Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                    <Button
                      fullWidth variant="contained" color="success" startIcon={<CheckIcon />}
                      sx={{ mt: 2 }}
                      disabled={delivering === order.id}
                      onClick={() => confirmDelivery(order)}
                    >
                      {delivering === order.id ? 'Confirmando...' : 'Confirmar entrega al cliente'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Comandas;
